import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';
import { highlights } from '../../data/products';

const HighlightsSection = () => {
  const [activeTab, setActiveTab] = useState('birthday');
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const tabs = [
    { id: 'birthday', label: 'Birthday' },
    { id: 'anniversary', label: 'Anniversary' },
    { id: 'babyShower', label: 'Baby Shower' },
    { id: 'gifting', label: 'Gifting' }
  ];

  // Update scroll progress and button states
  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;
    const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;

    setScrollProgress(progress);
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < maxScroll - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [activeTab, highlights]);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#8B1538] mb-6 sm:mb-8">
          This Week Highlight's
        </h2>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-6 sm:mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-[#8B1538] text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="relative">
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto gap-3 sm:gap-6 pb-4 scrollbar-hide"
          >
            {highlights[activeTab]?.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Scroll progress indicator and navigation buttons */}
          <div className="flex items-center justify-end gap-4 mt-4">
            {/* Scroll progress bar */}
            <div className="flex-1 max-w-[200px] h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#8B1538] transition-all duration-300 rounded-full"
                style={{ width: `${scrollProgress}%` }}
              />
            </div>

            {/* Navigation buttons */}
            <div className="flex gap-2">
              <button
                onClick={scrollLeft}
                disabled={!canScrollLeft}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  canScrollLeft
                    ? 'border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538] hover:text-white cursor-pointer'
                    : 'border-gray-300 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={scrollRight}
                disabled={!canScrollRight}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  canScrollRight
                    ? 'border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538] hover:text-white cursor-pointer'
                    : 'border-gray-300 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* View All Button */}
        <div className="text-center mt-6 sm:mt-8">
          <button
            onClick={() => {
              const sectionMap = {
                birthday: 'highlights-birthday',
                anniversary: 'highlights-anniversary',
                babyShower: 'highlights-baby-shower',
                gifting: 'highlights-gifting'
              };
              navigate(`/products?section=${sectionMap[activeTab] || 'highlights-birthday'}`);
            }}
            className="bg-[#8B1538] hover:bg-[#6B0F2A] text-white font-semibold px-8 sm:px-12 py-3 sm:py-4 rounded-lg text-sm sm:text-base transition-colors duration-300 cursor-pointer"
          >
            View All
          </button>
        </div>
      </div>
    </section>
  );
};

export default HighlightsSection;

